// ./src/core/Web/services/UserService.ts

import { API_CONFIG } from '@Web/config/api.config';
import { IRestClient } from '@Web/clients/Interfaces/IRestClient';
import { RestClient } from '@Web/clients/RestClient';
import { AuthService } from '@core/Web/services/AuthService';

// --- Interfaces ---

// What the Game Controller expects
export interface UserBalance {
  balance: number;
  currency: string;
}

// Raw API Response Types
interface ApiBalanceItem {
  type: string; // e.g., "CASH"
  amount: number;
  [key: string]: any;
}

interface ApiBalanceResponse {
  playerId: string;
  currency: string;
  balances: ApiBalanceItem[];
  [key: string]: any;
}

export class UserService {
  private static instance: UserService;
  private restClient: IRestClient;

  // Singleton Pattern
  public static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  private constructor() {
    // Initialize RestClient with the Base URL from config
    this.restClient = new RestClient(API_CONFIG.baseUrl);
  }

  /**
   * Fetches the user's current wallet balance.
   * @param authToken - Optional. If not provided, attempts to get it from AuthService.
   */
  public async getBalance(authToken?: string): Promise<UserBalance> {
    // 1. Resolve Token
    const token = authToken || AuthService.getInstance().getAccessToken();

    if (!token) {
      console.warn(
        '[UserService] Cannot fetch balance: No auth token provided or available.'
      );
      return { balance: 0, currency: 'EUR' }; // Default fallback
    }

    try {
      // 2. Make the request using RestClient
      // Note: RestClient usually handles the BaseURL, so we just pass the endpoint path
      const data = await this.restClient.get<ApiBalanceResponse>(
        API_CONFIG.endpoints.balance,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // 3. Process Logic (Find CASH wallet)
      const cashBalance = data.balances.find(b => b.type === 'CASH');

      return {
        balance: cashBalance ? cashBalance.amount : 0,
        currency: data.currency || 'EUR',
      };
    } catch (error) {
      console.error('[UserService] Failed to fetch balance:', error);
      throw error; // Re-throw to be handled by the caller (Controller)
    }
  }
}
